import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, FileText, Loader2, AlertCircle, ExternalLink } from 'lucide-react';

/**
 * Inline attachment preview modal. Renders directly on the page (portal to body)
 * so it works inside scrollable containers / dialogs.
 *
 * Supported inline previews:
 *   • PDF      → iframe
 *   • Images   → <img>
 *   • Text     → <pre>
 *   • Else     → fallback message + Download / Open externally
 */

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg', 'heic', 'heif'];
const TEXT_EXT = ['txt', 'csv', 'log', 'md', 'json', 'xml'];

function detectKind(att) {
  const type = (att?.type || '').toLowerCase();
  const url = att?.data_url || att?.url || '';
  const name = (att?.name || '').toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() : '';

  if (type.startsWith('image/') || IMAGE_EXT.includes(ext)) return 'image';
  if (type === 'application/pdf' || ext === 'pdf' || url.startsWith('data:application/pdf')) return 'pdf';
  if (type.startsWith('text/') || TEXT_EXT.includes(ext)) return 'text';
  return 'other';
}

function humanSize(b) {
  if (!b && b !== 0) return '';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

export default function AttachmentPreview({ attachment, onClose, onRemove }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [textContent, setTextContent] = useState('');

  const kind = useMemo(() => detectKind(attachment), [attachment]);
  const url = attachment?.data_url || attachment?.url || '';

  useEffect(() => {
    setLoading(true); setError(false); setTextContent('');
    if (!attachment) return;
    if (kind === 'text' && url.startsWith('data:')) {
      try {
        // data:text/plain;base64,... → decode
        const comma = url.indexOf(',');
        if (comma > -1) {
          const meta = url.slice(0, comma);
          const body = url.slice(comma + 1);
          const decoded = meta.includes(';base64') ? atob(body) : decodeURIComponent(body);
          setTextContent(decoded);
        }
        setLoading(false);
      } catch { setError(true); setLoading(false); }
    } else if (kind === 'image' || kind === 'pdf') {
      // iframe / img onLoad will flip loading off
    } else {
      setLoading(false);
    }
  }, [attachment, kind, url]);

  // Close on Escape
  useEffect(() => {
    if (!attachment) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attachment, onClose]);

  if (!attachment) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="attachment-preview"
      role="dialog"
      aria-modal="true"
      aria-label="Attachment preview"
    >
      <div
        className="relative w-full max-w-5xl max-h-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E8E6E1] shrink-0">
          <FileText size={14} strokeWidth={1.5} className="text-[#3A8C8C] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#2C2C2E] truncate" data-testid="attachment-preview-name">{attachment.name}</p>
            <p className="text-[10px] text-[#6B7280]">
              {kind.toUpperCase()}{attachment.size ? ` · ${humanSize(attachment.size)}` : ''}
            </p>
          </div>
          <a
            href={url}
            download={attachment.name}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#3A8C8C]/10 text-[#3A8C8C] text-xs font-medium hover:bg-[#3A8C8C]/15 transition-colors shrink-0"
            data-testid="attachment-preview-download"
          >
            <Download size={12} /> Download
          </a>
          {onRemove && (
            <button
              type="button"
              onClick={() => { onRemove(attachment); onClose?.(); }}
              className="text-xs text-[#C4727F] hover:underline shrink-0"
              data-testid="attachment-preview-remove"
            >
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[#E8E6E1] shrink-0"
            data-testid="attachment-preview-close"
            aria-label="Close preview"
          >
            <X size={14} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-[60vh] max-h-[80vh] overflow-auto bg-[#FAFAF8] flex items-center justify-center relative" data-testid="attachment-preview-body">
          {loading && (kind === 'image' || kind === 'pdf') && !error && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="attachment-preview-loading">
              <Loader2 size={22} className="text-[#3A8C8C] animate-spin" />
            </div>
          )}

          {error && (
            <div className="text-center px-6" data-testid="attachment-preview-error">
              <AlertCircle size={28} className="text-[#C4727F] mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#2C2C2E] font-medium">Failed to load file</p>
              <p className="text-xs text-[#6B7280] mt-1">The attachment could not be displayed. You can still download it.</p>
            </div>
          )}

          {!error && kind === 'image' && (
            <img
              src={url}
              alt={attachment.name}
              className="max-w-full max-h-[78vh] object-contain"
              onLoad={() => setLoading(false)}
              onError={() => { setError(true); setLoading(false); }}
              data-testid="attachment-preview-image"
            />
          )}

          {!error && kind === 'pdf' && (
            <iframe
              src={url}
              className="w-full h-[78vh] bg-white"
              title={attachment.name}
              onLoad={() => setLoading(false)}
              onError={() => { setError(true); setLoading(false); }}
              data-testid="attachment-preview-iframe"
            />
          )}

          {!error && kind === 'text' && (
            <pre
              className="w-full max-h-[78vh] overflow-auto p-4 text-xs font-mono text-[#2C2C2E] whitespace-pre-wrap"
              data-testid="attachment-preview-text"
            >
              {textContent || '(empty)'}
            </pre>
          )}

          {!error && kind === 'other' && (
            <div className="text-center px-6" data-testid="attachment-preview-fallback">
              <FileText size={28} className="text-[#D4D0C8] mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#2C2C2E] font-medium">Preview not available for this file type.</p>
              <p className="text-xs text-[#6B7280] mt-1">Download the file or open it externally to view.</p>
              <div className="flex gap-2 justify-center mt-4">
                <a href={url} download={attachment.name}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A]">
                  <Download size={12} /> Download
                </a>
                <a href={url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#D4D0C8] text-[#2C2C2E] text-xs font-medium hover:bg-[#E8E6E1]">
                  <ExternalLink size={12} /> Open externally
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
